import type { IRunPlan, IScaffold } from '../types';
import { logInfo } from '../services/log';
import { Scaffold } from './scaffold';

/**
 * render an `<iframe>` as the scaffold to load a remote view into
 */
export class HTMLFrameScaffold extends Scaffold implements IScaffold {
  /**
   * create an iframe and add attributes
   */
  async create(runPlan: IRunPlan) {
    const defaultAttrs = await this.createAttributes(runPlan);
    let iframeEl = document.createElement('iframe');

    iframeEl = this._mergeAttributes(iframeEl, runPlan, defaultAttrs);
    return iframeEl;
  }

  /**
   * find and erase the iframe tag created in startup
   */
  async destroy(runPlan: IRunPlan, renderTo: HTMLElement) {
    renderTo.innerHTML = '';
  }

  /**
   * choose the full index page
   */
  async weaveCode(head: string, body: string, index: string) {
    return index;
  }

  /**
   * inject code and convert to a uri with blob or just load a page
   */
  async injectCode<E extends HTMLElement>(scaffoldEl: E, code: string) {
    const _iframeEl = scaffoldEl as unknown as HTMLIFrameElement;
    const isURI = code.match(/^http/) != null;
    const uri = isURI ? code : this.__createBlob(code);

    _iframeEl.src = uri;
  }

  /**
   * @note we need to wait until the iframe is loaded and then return the body
   */
  async computeScriptContext<E extends HTMLElement>(scaffoldEl: E): Promise<HTMLElement> {
    const iframeEl = scaffoldEl as unknown as HTMLIFrameElement;
    const iframeWindow = iframeEl.contentWindow;
    if (!iframeWindow) {
      // error, we need to return the body
      //
      //
      return iframeEl as unknown as HTMLElement;
    }

    await new Promise((resolve) => {
      iframeWindow?.addEventListener('load', resolve);
    });

    const bodyEl = iframeWindow.document.body as unknown as HTMLElement;
    if (bodyEl) {
      return bodyEl as unknown as HTMLElement;
    }

    // error, we need to return the body
    //
    //
    return iframeEl as unknown as HTMLElement;
  }

  /**
   * @todo we need to add sandbox to iframes
   */
  async createAttributes(runPlan: IRunPlan): Promise<Record<string, string>> {
    return {
      name: `iframe-remote-view`
      // NOTE: if we allow all rules is there value in the sandbox?
      //
      //
      // sandbox: 'allow-downloads allow-forms allow-modals allow-popups allow-same-origin allow-popups-to-escape-sandbox allow-scripts',
      // do we want to give this?
      //
      // allow: 'display-capture fullscreen'
    };
  }

  /**
   * wait for the load event on the `<iframe>`
   */
  async beforeMount(scaffoldEl: HTMLElement, renderTo: HTMLElement, runPlan: IRunPlan) {
    scaffoldEl.addEventListener(
      'load',
      async function () {
        if (runPlan.mount) {
          await runPlan.mount(scaffoldEl, renderTo as HTMLElement);
          logInfo(renderTo, `run mount`);
        }
      },
      { once: true }
    );
  }

  /**
   * no action needed because we waited for `load` in `beforeMount`
   */
  async afterMount(scaffoldEl: HTMLElement, renderTo: HTMLElement, runPlan: IRunPlan) {
    return;
  }

  /**
   * generate the code as a blob url so we can use `src` and not `srcdoc` because `srcdoc`
   * has load issues
   */
  private __createBlob(code: string) {
    const blob = new Blob([code], { type: 'text/html' });

    return window.URL.createObjectURL(blob);
  }
}
